/**
 * Reviewer Token Service
 * Manages JWT tokens for reviewer authentication in localStorage
 */

import { getServerUrl } from '@/utils/';

interface ReviewerTokens {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: string;
  expiresAt: string;
}

interface ReviewerInfo {
  id: string;
  email: string;
  assessmentId: string;
  candidateId: string;
  role: string[];
  status: string;
  reviewExpireAt?: string;
  isReviewExpired?: boolean;
}

interface AuthData {
  tokens: ReviewerTokens;
  reviewer: ReviewerInfo;
}

// Backend response structure from OTP verification
interface AuthResponse {
  success: boolean;
  message: string;
  reviewerEmail: string;
  candidateId: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: string;
  expiresAt: string;
  tokenType?: string;
  reviewer: ReviewerInfo;
}

class ReviewerTokenService {
  private static readonly ACCESS_TOKEN_KEY = 'reviewer_access_token';
  private static readonly REFRESH_TOKEN_KEY = 'reviewer_refresh_token';
  private static readonly TOKEN_TYPE_KEY = 'reviewer_token_type';

  /**
   * Store authentication data in localStorage
   */
  static storeAuthData(authData: AuthData): void {
    try {
      localStorage.setItem(this.ACCESS_TOKEN_KEY, authData.tokens.accessToken);
      localStorage.setItem(this.REFRESH_TOKEN_KEY, authData.tokens.refreshToken);
      localStorage.setItem(this.TOKEN_TYPE_KEY, authData.tokens.tokenType || 'Bearer');
    } catch (error) {
      console.error('Error storing reviewer auth data:', error);
    }
  }

  /**
   * Get access token from localStorage
   */
  static getAccessToken(): string | null {
    try {
      return localStorage.getItem(this.ACCESS_TOKEN_KEY);
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  }

  /**
   * Get refresh token from localStorage
   */
  static getRefreshToken(): string | null {
    try {
      return localStorage.getItem(this.REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error('Error getting refresh token:', error);
      return null;
    }
  }

  /**
   * Get reviewer information from JWT token
   */
  static getReviewerInfo(): ReviewerInfo | null {
    try {
      const accessToken = this.getAccessToken();
      if (!accessToken) return null;

      // Decode JWT token to get reviewer info
      const payload = this.decodeJWTPayload(accessToken);
      if (!payload) return null;

      return {
        id: payload.reviewerId || payload.id,
        email: payload.email,
        assessmentId: payload.assessmentId,
        candidateId: payload.candidateId,
        role: payload.role || [],
        status: payload.status || 'active',
        reviewExpireAt: payload.reviewExpireAt,
        isReviewExpired: payload.reviewExpireAt ? new Date() > new Date(payload.reviewExpireAt) : false
      };
    } catch (error) {
      console.error('Error getting reviewer info from JWT:', error);
      return null;
    }
  }

  /**
   * Get reviewer information with automatic refresh if needed
   */
  static async getReviewerInfoWithRefresh(): Promise<ReviewerInfo | null> {
    let info = this.getReviewerInfo();
    
    // If no info but we have tokens, try refreshing
    if (!info && this.getAccessToken() && this.getRefreshToken()) {
      console.log('Attempting to refresh token to get reviewer info...');
      const refreshSuccess = await this.refreshAccessToken();
      if (refreshSuccess) {
        info = this.getReviewerInfo();
      }
    }
    
    return info;
  }

  /**
   * Decode JWT payload (client-side, for non-sensitive data only)
   */
  private static decodeJWTPayload(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT payload:', error);
      return null;
    }
  }

  /**
   * Get authorization header for API requests
   */
  static getAuthHeader(): string | null {
    const accessToken = this.getAccessToken();
    const tokenType = localStorage.getItem(this.TOKEN_TYPE_KEY) || 'Bearer';
    
    if (accessToken) {
      return `${tokenType} ${accessToken}`;
    }
    
    return null;
  }

  /**
   * Update both access and refresh tokens (used after refresh)
   */
  static updateTokens(accessToken: string, refreshToken: string, tokenType: string = 'Bearer'): void {
    try {
      localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
      localStorage.setItem(this.TOKEN_TYPE_KEY, tokenType);
    } catch (error) {
      console.error('Error updating tokens:', error);
    }
  }

  /**
   * Update access token (used after refresh)
   */
  static updateAccessToken(accessToken: string, tokenType: string = 'Bearer'): void {
    try {
      localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
      localStorage.setItem(this.TOKEN_TYPE_KEY, tokenType);
    } catch (error) {
      console.error('Error updating access token:', error);
    }
  }

  /**
   * Check if reviewer is authenticated (with potential for refresh)
   */
  static isAuthenticated(): boolean {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    
    // If no tokens at all, definitely not authenticated
    if (!accessToken || !refreshToken) return false;
    
    // Check if access token is still valid
    const payload = this.decodeJWTPayload(accessToken);
    if (!payload || !payload.exp) return false;
    
    // Check if token is expired (with 30 second buffer)
    const now = Math.floor(Date.now() / 1000);
    const isAccessTokenValid = payload.exp > (now + 30);
    
    // If access token is valid, we're authenticated
    if (isAccessTokenValid) return true;
    
    // If access token is expired, check if refresh token is still valid
    const refreshPayload = this.decodeJWTPayload(refreshToken);
    if (!refreshPayload || !refreshPayload.exp) {
      // Refresh token is also invalid, clear everything
      this.clearAuthData();
      return false;
    }
    
    // Check if refresh token is expired
    const isRefreshTokenValid = refreshPayload.exp > now;
    if (!isRefreshTokenValid) {
      // Both tokens are expired, clear everything
      console.log('Both access and refresh tokens are expired, clearing auth data');
      this.clearAuthData();
      return false;
    }
    
    // Access token expired but refresh token is valid - we can potentially refresh
    return true;
  }

  /**
   * Check if reviewer has valid access token (no grace period for refresh)
   */
  static hasValidAccessToken(): boolean {
    const accessToken = this.getAccessToken();
    if (!accessToken) return false;
    
    const payload = this.decodeJWTPayload(accessToken);
    if (!payload || !payload.exp) return false;
    
    const now = Math.floor(Date.now() / 1000);
    return payload.exp > (now + 30);
  }

  /**
   * Clear all authentication data
   */
  static clearAuthData(): void {
    try {
      localStorage.removeItem(this.ACCESS_TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      localStorage.removeItem(this.TOKEN_TYPE_KEY);
      console.log('Reviewer authentication data cleared');
    } catch (error) {
      console.error('Error clearing auth data:', error);
    }
  }

  /**
   * Force logout - clears auth data and triggers page reload
   */
  static forceLogout(): void {
    console.log('Forcing reviewer logout due to token expiration');
    this.clearAuthData();
    
    // Also clear any lingering reviewer auth context data
    try {
      // Clear any other potential auth-related data
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('reviewer_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('Error clearing additional auth data:', error);
    }
    
    // Small delay to ensure storage is cleared
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }

  /**
   * Refresh access token using refresh token
   */
  static async refreshAccessToken(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    
    if (!refreshToken) {
      this.clearAuthData();
      return false;
    }

    try {
      const baseUrl = getServerUrl();
      const response = await fetch(`${baseUrl}/review/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Token refresh response:', { 
          hasAccessToken: !!data.accessToken, 
          hasRefreshToken: !!data.refreshToken, 
          tokenType: data.tokenType 
        });
        // Update both access and refresh tokens
        this.updateTokens(data.accessToken, data.refreshToken, data.tokenType);
        return true;
      } else {
        console.error('Token refresh failed:', response.status, response.statusText);
        // Refresh token is invalid or expired - force logout
        this.forceLogout();
        return false;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      // Network error or other issues - force logout
      this.forceLogout();
      return false;
    }
  }

  /**
   * Logout reviewer
   */
  static async logout(): Promise<void> {
    const authHeader = this.getAuthHeader();
    
    if (authHeader) {
      try {
        const baseUrl = getServerUrl();
        await fetch(`${baseUrl}/review/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': authHeader,
            'Content-Type': 'application/json',
          },
        });
      } catch (error) {
        console.error('Error during logout API call:', error);
      }
    }

    this.clearAuthData();
  }

  /**
   * Make authenticated API request with automatic token refresh
   */
  static async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    let authHeader = this.getAuthHeader();
    
    if (!authHeader) {
      throw new Error('No authentication token available');
    }

    // Construct full URL if it's a relative path
    const fullUrl = url.startsWith('http') ? url : `${getServerUrl()}${url.startsWith('/') ? url : `/${url}`}`;

    // Add authorization header to the request
    const requestOptions: RequestInit = {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': authHeader,
        'Content-Type': 'application/json',
      },
    };

    let response = await fetch(fullUrl, requestOptions);

    // If token expired, try to refresh and retry
    if (response.status === 401) {
      const refreshSuccess = await this.refreshAccessToken();
      
      if (refreshSuccess) {
        // Retry with new token
        authHeader = this.getAuthHeader();
        if (authHeader) {
          requestOptions.headers = {
            ...requestOptions.headers,
            'Authorization': authHeader,
          };
          response = await fetch(fullUrl, requestOptions);
        }
      } else {
        // Refresh failed, redirect to login
        this.clearAuthData();
        throw new Error('Authentication failed');
      }
    }

    return response;
  }
}

export default ReviewerTokenService;
